import React, { useState, useCallback, useEffect } from "react";
import { observer } from "mobx-react-lite";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import { FormControl, IconButton, InputAdornment, InputLabel, OutlinedInput } from "@mui/material";

import {
  TranslationContext,
  translationService,
} from "../../services/translation/TranslationService";
import "./NumberSelection.scss";

interface NumberSelectionProps {
  initialValue: number;
  minValue: number;
  maxValue: number;
  labelTranslationKey: string;
  labelTranslationContext?: TranslationContext;
  minusButtonDisabled?: boolean;
  plusButtonDisabled?: boolean;
  step?: number;
  callback: (newValue: number) => void;
}

export const NumberSelection = observer((props: NumberSelectionProps) => {
  const {
    initialValue,
    minValue,
    maxValue,
    labelTranslationKey,
    labelTranslationContext,
    minusButtonDisabled = false,
    plusButtonDisabled = false,
    step = 1,
    callback,
  } = props;

  const [selectedNumber, setSelectedNumber] = useState(() => {
    return initialValue;
  });

  const changeCallback = useCallback(
    (newValue: number) => {
      const getValidatedValue = (value: any) => {
        if (isNaN(parseInt(value, 10))) return minValue;
        if (parseInt(value, 10) !== value) return minValue;
        if (value < minValue) return minValue;
        if (value > maxValue) return maxValue;

        return value;
      };

      newValue = getValidatedValue(newValue);

      setSelectedNumber(newValue);
      callback(newValue);
    },
    [minValue, maxValue, callback]
  );

  useEffect(() => {
    setSelectedNumber(initialValue);
  }, [initialValue]);

  return (
    <>
      <FormControl style={{ width: "140px", margin: "15px 0 0 0" }}>
        <InputLabel>
          {translationService.translate(labelTranslationKey, labelTranslationContext)}
        </InputLabel>
        <OutlinedInput
          value={selectedNumber}
          inputProps={{ style: { textAlign: "center" } }}
          className="counter-input"
          label={translationService.translate(labelTranslationKey, labelTranslationContext)}
          onChange={(e) => setSelectedNumber(Number(e.target.value))}
          onBlur={(e) => {
            const newValue = Number(e.target.value);
            changeCallback(Math.round(newValue / step) * step);
          }}
          startAdornment={
            <InputAdornment position="start">
              <IconButton
                edge="start"
                disabled={minusButtonDisabled}
                onClick={() => changeCallback(selectedNumber - step)}
              >
                <RemoveIcon className={selectedNumber > minValue ? "active-button" : undefined} />
              </IconButton>
            </InputAdornment>
          }
          endAdornment={
            <InputAdornment position="end">
              <IconButton
                edge="end"
                disabled={plusButtonDisabled}
                onClick={() => changeCallback(selectedNumber + step)}
              >
                <AddIcon className={selectedNumber < maxValue ? "active-button" : undefined} />
              </IconButton>
            </InputAdornment>
          }
        />
      </FormControl>
    </>
  );
});
